// src/pages/Login.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { User, Lock, AlertCircle, Loader, Eye, EyeOff, KeyRound, ArrowLeft, ShieldCheck } from 'lucide-react';
import { toast } from "react-toastify";
import { validate, isValidEmail, isRequired, sanitizeInput, ValidationSchema } from '../utils/validation';
import { MESSAGES } from '../utils/messages';

const loginSchema: ValidationSchema = {
  email: [isRequired('Email'), isValidEmail],
  password: [isRequired('Password')],
};

const otpSchema: ValidationSchema = {
  otp: [isRequired('Verification code')],
};

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const handleBlur = (field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const fieldErrors = validate({ [field]: loginSchema[field] }, { email, password });
    setErrors((prev) => ({ ...prev, ...fieldErrors }));
  };

  const validateAll = () => {
    const allErrors = validate(loginSchema, { email, password });
    setErrors(allErrors);
    setTouched({ email: true, password: true });
    return Object.keys(allErrors).length === 0;
  };

  const inputClass = (field: string, base = 'input-field pl-10 block w-full rounded-md sm:text-sm') => {
    const hasError = errors[field] && touched[field];
    return `${base} ${
      hasError
        ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
        : 'border-neutral-300 focus:ring-primary-300 focus:border-primary-500'
    }`;
  };
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { loginDoctor, loginAdmin, verifyMfaOtp, resendMfaOtp } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdminLogin = location.pathname === '/admin/login';

  // ── MFA state ──────────────────────────────────────────────
  const [mfaMode, setMfaMode] = useState(false);
  const [mfaToken, setMfaToken] = useState('');
  const [otp, setOtp] = useState('');
  const [otpErrors, setOtpErrors] = useState<Record<string, string>>({});
  const [otpTouched, setOtpTouched] = useState<Record<string, boolean>>({});
  const [resendCooldown, setResendCooldown] = useState(0);
  const [expirySeconds, setExpirySeconds] = useState(0);
  const [devOtp, setDevOtp] = useState('');
  const otpInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (mfaMode) {
      otpInputRef.current?.focus();
    }
  }, [mfaMode]);

  // Resend cooldown countdown
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  // OTP expiry countdown
  useEffect(() => {
    if (expirySeconds <= 0) return;
    const timer = setInterval(() => {
      setExpirySeconds((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [expirySeconds]);

  const enterMfaMode = (token: string, expiry: number, devOtpCode?: string) => {
    setMfaToken(token);
    setOtp('');
    setOtpErrors({});
    setOtpTouched({});
    setExpirySeconds(expiry);
    setResendCooldown(60);
    setError('');
    setDevOtp(devOtpCode || '');
    setMfaMode(true);
  };

  const exitMfaMode = () => {
    setMfaMode(false);
    setMfaToken('');
    setOtp('');
    setOtpErrors({});
    setExpirySeconds(0);
    setResendCooldown(0);
    setDevOtp('');
    setPassword('');
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateAll()) {
      return;
    }

    setIsLoading(true);
  
    try {
      const result = isAdminLogin
        ? await loginAdmin(email, password)
        : await loginDoctor(email, password);
  
      if (result.success && result.requiresMfa && result.mfaToken) {
        enterMfaMode(result.mfaToken, result.mfa?.expirySeconds || 300, result.mfa?.otp);
        toast.info("A verification code has been sent to your email.");
        setIsLoading(false);
        return;
      }

      if (result.success) {
        if (result.needsPasswordReset) {
          toast.info(MESSAGES.info.needsPasswordReset);
          setIsLoading(false);
          return navigate("/set-password");
        } else {
          toast.success(MESSAGES.success.login);
          localStorage.getItem("token");
          setIsLoading(false);
          return navigate("/dashboard");
        }
      } else {
        const msg = result.errorMessage || MESSAGES.error.invalidCredentials;
        setError(msg);
        toast.error(msg);
      }
    } catch (err: any) {
      setPassword('');
      setError(MESSAGES.error.credentialsError);
      toast.error(MESSAGES.error.credentialsError);
    } finally {
      setIsLoading(false);
    }
  };

  const validateOtp = () => {
    const allErrors = validate(otpSchema, { otp });
    setOtpErrors(allErrors);
    setOtpTouched({ otp: true });
    return Object.keys(allErrors).length === 0;
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateOtp()) {
      return;
    }

    setIsLoading(true);
    try {
      const result = await verifyMfaOtp(mfaToken, otp);
      if (result.success) {
        toast.success(MESSAGES.success.login);
        setMfaMode(false);
        setIsLoading(false);
        return navigate("/dashboard");
      }
      const msg = result.errorMessage || "Invalid verification code.";
      setError(msg);
      toast.error(msg);
      setOtp('');
    } catch (err: any) {
      setError(MESSAGES.error.invalidOrExpiredOtp || "Verification failed. Please try again.");
      toast.error(MESSAGES.error.invalidOrExpiredOtp || "Verification failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;
    setError('');
    setIsLoading(true);
    try {
      const result = await resendMfaOtp(mfaToken);
      if (result.success) {
        setOtp('');
        setOtpErrors({});
        setExpirySeconds(result.mfa?.expirySeconds || 300);
        setResendCooldown(60);
        if (result.mfa?.otp) setDevOtp(result.mfa.otp);
        toast.success("A new verification code has been sent.");
      } else {
        const msg = result.errorMessage || "Failed to resend the verification code.";
        setError(msg);
        toast.error(msg);
      }
    } catch (err: any) {
      setError("Failed to resend the verification code.");
      toast.error("Failed to resend the verification code.");
    } finally {
      setIsLoading(false);
    }
  };
  
  // OLD: OTP flow handlers (kept for reference)
  // const handleForgotPassword = async (e: React.FormEvent) => {
  //   e.preventDefault();
  //   setError('');
  //   setIsLoading(true);
  //   try {
  //     await forgotPassword(forgotPasswordEmail);
  //     toast.success(MESSAGES.success.otpSent);
  //     setMode('otp');
  //   } catch {
  //     setError(MESSAGES.error.failedToSendOtp);
  //     toast.error(MESSAGES.error.failedToSendOtp);
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };
  //
  // const handleVerifyOtp = async (e: React.FormEvent) => {
  //   e.preventDefault();
  //   setError('');
  //   setIsLoading(true);
  //   try {
  //     await verifyResetOtp(forgotPasswordEmail, otp);
  //     toast.success(MESSAGES.success.otpVerified);
  //     setMode('reset');
  //   } catch {
  //     setError(MESSAGES.error.invalidOrExpiredOtp);
  //     toast.error(MESSAGES.error.invalidOrExpiredOtp);
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };
  //
  // const handleResetPassword = async (e: React.FormEvent) => {
  //   e.preventDefault();
  //   setError('');
  //   if (newPassword !== confirmPassword) {
  //     setError(MESSAGES.error.passwordMismatch);
  //     return;
  //   }
  //   setIsLoading(true);
  //   try {
  //     await resetPassword(forgotPasswordEmail, newPassword, confirmPassword);
  //     toast.success(MESSAGES.success.passwordReset);
  //     setMode('login');
  //     setForgotPasswordEmail('');
  //     setOtp('');
  //     setNewPassword('');
  //     setConfirmPassword('');
  //   } catch {
  //     setError(MESSAGES.error.passwordResetFailed);
  //     toast.error(MESSAGES.error.passwordResetFailed);
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };

  const handleSubmit = (e: React.FormEvent) => {
    if (mfaMode) {
      handleVerifyOtp(e);
    } else {
      handleLogin(e);
    }
  };

  // const demoCredentials = [
  //   { role: 'Consultant', email: 'dr.smith@hospital.com', password: 'password' },
  //   { role: 'Junior Doctor', email: 'dr.jones@hospital.com', password: 'password' },
  //   { role: 'Super Admin', email: 'admin@hospital.com', password: 'password' }
  // ];

  // const handleAutofill = (email: string, password: string) => {
  //   setEmail(email);
  //   setPassword(password);
  //   setError('');
  // };

  return (
    <div className="h-screen w-full flex overflow-hidden">
      {/* Left Banner Section */}
      <div className="hidden lg:flex w-1/2 relative bg-neutral-900">
        <img 
          src="/logo/DiagnomIQ-loginpage-banner.jpg" 
          alt="Login Banner" 
          className="w-full h-full object-cover opacity-80"
        />
      </div>

      {/* Right Login Form Section */}
      <div className="flex-1 flex items-center justify-center bg-[#F8FAFA] px-4 sm:px-6 lg:px-8 overflow-y-auto">
        <div className="max-w-md w-full space-y-8 py-12">
          <div className="text-center">
            <img
              src="/logo/DiagnomIQ.png"
              alt="AI Patient Care Platform"
              className="mx-auto h-24 w-auto object-contain"
            />
            {isAdminLogin && (
              <span className="inline-block mt-2 px-3 py-1 text-xs font-semibold text-primary-700 bg-primary-100 rounded-full">
                Admin Portal
              </span>
            )}
            <p className="mt-3 text-sm text-neutral-600">
              {isAdminLogin
                ? 'Sign in to access the administration dashboard'
                : 'Sign in to access your personalized precision care treatment system'}
            </p>
          </div>

          <div className="bg-white py-8 px-6 shadow-xl rounded-xl border border-neutral-200">
            <form className="space-y-6" onSubmit={handleSubmit}>

                  {!mfaMode && (<>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-neutral-700">
                      Email Address
                    </label>
                    <div className="mt-1 relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <User className="h-5 w-5 text-neutral-400" />
                      </div>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onBlur={() => handleBlur('email')}
                        className={inputClass('email')}
                        placeholder="Enter your email"
                      />
                    </div>
                    {errors.email && touched.email && (
                      <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-neutral-700">
                      Password
                    </label>
                    <div className="mt-1 relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Lock className="h-5 w-5 text-neutral-400" />
                      </div>
                      <input
                        id="password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        autoComplete="new-password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onBlur={() => handleBlur('password')}
                        className={inputClass('password', 'input-field pl-10 pr-10 block w-full rounded-md sm:text-sm')}
                        placeholder="Enter your password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-neutral-400 hover:text-neutral-600 focus:outline-none"
                      >
                        {showPassword ? (
                          <EyeOff className="h-5 w-5" />
                        ) : (
                          <Eye className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                    {errors.password && touched.password && (
                      <p className="mt-1 text-sm text-red-600">{errors.password}</p>
                    )}
                  </div>

                  <div className="text-right mt-2">
                    {/* OLD: OTP flow - inline forgot password */}
                    {/* <button
                      type="button"
                      onClick={() => {
                        setMode('forgot');
                        setForgotPasswordEmail(email);
                        setError('');
                      }}
                      className="text-sm text-primary-600 hover:text-primary-500"
                    >
                      Forgot password?
                    </button> */}
                    <Link
                      to="/forgot-password"
                      className="text-sm text-primary-600 hover:text-primary-500 font-medium"
                    >
                      Forgot password?
                    </Link>
                  </div>
                  </>)}

                  {/* ── MFA verification step ───────────────────────── */}
                  {mfaMode && (
                    <>
                      <div className="flex flex-col items-center text-center space-y-2">
                        <div className="p-3 rounded-full bg-primary-100 text-primary-600">
                          <ShieldCheck className="h-8 w-8" />
                        </div>
                        <h2 className="text-lg font-semibold text-neutral-900">Two-Factor Verification</h2>
                        <p className="text-sm text-neutral-600">
                          A 6-digit verification code was sent to{' '}
                          <span className="font-medium text-neutral-900">{email}</span>.
                          Enter it below to complete signing in.
                        </p>
                        {expirySeconds > 0 && (
                          <p className="text-xs text-neutral-500">
                            Code expires in <span className="font-medium text-primary-600">{expirySeconds}s</span>
                          </p>
                        )}
                      </div>

                      <div>
                        <label htmlFor="otp" className="block text-sm font-medium text-neutral-700">
                          Verification Code
                        </label>
                        <div className="mt-1 relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <KeyRound className="h-5 w-5 text-neutral-400" />
                          </div>
                          <input
                            id="otp"
                            name="otp"
                            type="text"
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            required
                            maxLength={6}
                            value={otp}
                            onChange={(e) => setOtp(sanitizeInput(e.target.value).replace(/\D/g, '').slice(0, 6))}
                            onBlur={() => {
                              setOtpTouched({ otp: true });
                              const fe = validate(otpSchema, { otp });
                              setOtpErrors(fe);
                            }}
                            ref={otpInputRef}
                            className={inputClass('otp', 'input-field pl-10 block w-full rounded-md sm:text-sm tracking-[0.3em] text-center')}
                            placeholder="••••••"
                          />
                        </div>
                        {otpErrors.otp && otpTouched.otp && (
                          <p className="mt-1 text-sm text-red-600">{otpErrors.otp}</p>
                        )}
                      </div>

                      <div>
                        <button
                          type="button"
                          onClick={handleResendOtp}
                          disabled={resendCooldown > 0 || isLoading}
                          className="w-full text-sm text-primary-600 hover:text-primary-500 disabled:text-neutral-400 disabled:cursor-not-allowed transition-colors"
                        >
                          {resendCooldown > 0
                            ? `Resend code in ${resendCooldown}s`
                            : 'Resend code'}
                        </button>
                      </div>

                      <button
                        type="button"
                        onClick={exitMfaMode}
                        className="w-full inline-flex items-center justify-center text-sm text-neutral-500 hover:text-neutral-700 transition-colors"
                      >
                        <ArrowLeft className="h-4 w-4 mr-1" />
                        Back to login
                      </button>
                    </>
                  )}

              {/* OLD: OTP forgot email form (kept for reference) */}
              {/* {mode === 'forgot' && (
                <div>
                  <label htmlFor="forgot-email" className="block text-sm font-medium text-gray-700">
                    Email Address
                  </label>
                  <div className="mt-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="forgot-email"
                      name="forgot-email"
                      type="email"
                      autoComplete="email"
                      required
                      value={forgotPasswordEmail}
                      onChange={(e) => setForgotPasswordEmail(sanitizeInput(e.target.value))}
                      className="input-field pl-10 block w-full rounded-md border-gray-300 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      placeholder="Enter your registered email"
                    />
                  </div>
                </div>
              )} */}

              {/* OLD: OTP verification form (kept for reference) */}
              {/* {mode === 'otp' && (
                <div>
                  <label htmlFor="otp" className="block text-sm font-medium text-gray-700">
                    OTP Code
                  </label>
                  <div className="mt-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <KeyRound className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="otp"
                      name="otp"
                      type="text"
                      required
                      value={otp}
                      onChange={(e) => setOtp(sanitizeInput(e.target.value))}
                      className="input-field pl-10 block w-full rounded-md border-gray-300 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      placeholder="Enter OTP"
                    />
                  </div>
                </div>
              )} */}

              {/* OLD: Reset password form (kept for reference) */}
              {/* {mode === 'reset' && (
                <>
                  <div>
                    <label htmlFor="new-password" className="block text-sm font-medium text-gray-700">
                      New Password
                    </label>
                    <div className="mt-1 relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Lock className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        id="new-password"
                        name="new-password"
                        type={showNewPassword ? "text" : "password"}
                        autoComplete="new-password"
                        required
                        value={newPassword}
                        onChange={(e) => setNewPassword(sanitizeInput(e.target.value))}
                        className="input-field pl-10 pr-10 block w-full rounded-md border-gray-300 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        placeholder="Enter new password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
                      >
                        {showNewPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700">
                      Confirm Password
                    </label>
                    <div className="mt-1 relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Lock className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        id="confirm-password"
                        name="confirm-password"
                        type={showConfirmPassword ? "text" : "password"}
                        autoComplete="new-password"
                        required
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(sanitizeInput(e.target.value))}
                        className="input-field pl-10 pr-10 block w-full rounded-md border-gray-300 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        placeholder="Confirm new password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
                      >
                        {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>
                </>
              )} */}

              {error && (
                <div className="flex items-center space-x-2 text-[#1E293B] text-sm bg-[#fff1f5] p-3 rounded-lg border border-[#D96F91]">
                  <AlertCircle className="h-4 w-4 text-[#EA2A6D]" />
                  <span>{error}</span>
                </div>
              )}

              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="group relative w-full flex justify-center py-2.5 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-500 hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-300 transition-colors duration-200 shadow-sm"
                >
                  {isLoading ? (
                    <>
                      <Loader className="h-4 w-4 mr-2 animate-spin" />
                      {mfaMode ? 'Verifying...' : 'Signing in...'}
                    </>
                  ) : (
                    mfaMode ? 'Verify & Sign In' : 'Sign in'
                  )}
                </button>
              </div>

              <div className="text-center">
                {!mfaMode && (
                  <p className="text-sm text-neutral-600">
                    Don't have an account?{' '}
                    <Link
                      to="/register"
                      className="font-medium text-primary-600 hover:text-primary-500 transition-colors"
                    >
                      Register
                    </Link>
                  </p>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Dev/QA OTP badge — only visible in non-production */}
      {mfaMode && devOtp && process.env.NODE_ENV !== 'production' && (
        <div className="fixed bottom-4 right-4 z-50 bg-neutral-900/90 text-white px-4 py-2 rounded-lg shadow-lg text-sm font-mono backdrop-blur-sm">
          <span className="opacity-60">Dev OTP:</span>{' '}
          <span className="font-bold tracking-widest">{devOtp}</span>
        </div>
      )}
    </div>
  );
};

export default Login;
